import { toast } from "sonner";
import { api } from "@/lib/api";
import { SI } from "@/lib/sourcing";

const save = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); };
const vals = (xs, k = "value") => (xs || []).map((x) => (typeof x === "string" ? x : x[k])).filter(Boolean);
const day = () => new Date().toISOString().slice(0, 10);

// §75 Everyone matching the filters (qs) or just the ticked ids, with contact details.
export const fetchExportRows = async (qs, ids) => {
  const url = ids?.length ? `${SI}/talent?ids=${ids.map(encodeURIComponent).join(",")}&include=contact&limit=5000` : `${SI}/talent?${qs}&include=contact&limit=5000`;
  const { data } = await api.get(url);
  const items = data?.items || [];
  if (data?.total > items.length) toast.warning(`Exporting the first ${items.length} of ${data.total} people. Narrow the filters to get the rest.`);
  return items.map((x) => ({
    Name: x.full_name || "", Title: x.current_title || "", Company: x.current_employer || "", Location: x.location || "",
    Emails: vals(x.emails).join("; "), Phones: vals(x.phones).join("; "), LinkedIn: x.linkedin_url || vals(x.links, "url").find((u) => /linkedin\.com/i.test(u)) || "",
    "Other links": vals(x.links, "url").filter((u) => !/linkedin\.com/i.test(u)).join(" "), Skills: (x.skills || []).join(", "),
    "Resume updated": (x.resume_updated_at || x.added_at || "").slice(0, 10), Source: x.source || "",
  }));
};

export const saveXlsx = async (rows) => {
  const X = await import("xlsx");
  const wb = X.utils.book_new();
  X.utils.book_append_sheet(wb, X.utils.json_to_sheet(rows), "Internal talent");
  X.writeFile(wb, `internal-talent-${day()}.xlsx`);
};

export const saveCsv = (rows) => {
  const cols = Object.keys(rows[0] || {});
  const esc = (v) => { const s = String(v ?? ""); return `"${(/^[=+\-@\t\r]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`; };
  save(new Blob(["\uFEFF" + [cols.map(esc).join(","), ...rows.map((r) => cols.map((k) => esc(r[k])).join(","))].join("\n")], { type: "text/csv;charset=utf-8" }), `internal-talent-${day()}.csv`);
};

const EXT = { "application/pdf": "pdf", "application/msword": "doc", "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx" };
const fileName = (h, type, who) => {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(h || "");
  const ext = (m && m[1].split(".").pop()) || EXT[(type || "").split(";")[0]] || "pdf";
  return `${(who || "resume").replace(/[\\/:*?"<>|]+/g, " ").trim()}.${ext}`;
};

// Downloads each person's original resume and zips them; returns {added, missing:[names]}.
export const saveResumeZip = async (people, onStep) => {
  const JSZip = (await import("jszip")).default;
  const z = new JSZip(); const used = new Set(); const missing = []; let n = 0;
  for (const p of people) {
    onStep?.(n + missing.length + 1, people.length);
    try {
      const r = await api.get(`${SI}/talent/${p.id}/resume/file`, { responseType: "blob" });
      let name = fileName(r.headers?.["content-disposition"], r.headers?.["content-type"] || r.data?.type, p.full_name);
      for (let i = 2; used.has(name.toLowerCase()); i++) name = name.replace(/( \(\d+\))?(\.\w+)$/, ` (${i})$2`);
      used.add(name.toLowerCase()); z.file(name, r.data); n++;
    } catch { missing.push(p.full_name || p.id); }
  }
  if (n) save(await z.generateAsync({ type: "blob" }), `internal-talent-resumes-${day()}.zip`);
  return { added: n, missing };
};
